import { toast } from "react-toastify"
import styled from "styled-components"

import { contractorsApi } from "store/api/contractorsApi"
import { useAppDispatch } from "store/hooks"
import { ApiTypes, UITypes } from "types"
import type { TableActionButtonGroup } from "types/table/tableActionButton"
import { ReactComponent as IconEye } from "assets/svg/icon-eye.svg"
import { ReactComponent as IconFile } from "assets/svg/icon-file.svg"

import { isMobileDevice } from "utils/deviceDetection"
import { Pagination } from "components/common/Pagination"
import TableWrapper from "components/table/TableWrapper"

import intelligenceReportsTableHeaderCells from "../intelligenceReportsTableHeaderCells"
import type { IntelligenceReportDrawerRow } from "./IntelligenceReportDrawer"

const TableSection = styled.div`
  flex: 1;

  .rowNavButtonsContainer {
    justify-content: center;
    align-items: center;
  }

  .tableActionsWrapper {
    background: linear-gradient(90deg, rgba(248, 250, 252, 0.7) 0%, #f1f5f9 100%);
    justify-content: flex-start;
    align-items: center;
    gap: 8px;
  }

  .fullReportAction {
    padding: 0 !important;
    width: auto !important;
    display: flex;
    align-items: center;

    span {
      height: auto;
      padding: 0 !important;
      width: auto !important;
    }

    span:hover,
    &:hover {
      background-color: transparent !important;
      opacity: 0.9 !important;
    }
  }
`

const FullReportText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #2db2c8;
  white-space: nowrap;
`

interface Props {
  data: UITypes.TableBodyRow[]
  isLoading: boolean
  meta?: ApiTypes.Model.Pagination
  onViewReport: (item: IntelligenceReportDrawerRow) => void
}

export const IntelligenceReportsTable = ({ data, isLoading, meta, onViewReport }: Props) => {
  const isMobile = isMobileDevice()
  const dispatch = useAppDispatch()

  const getRowActions = (rowData: UITypes.TableBodyRow): TableActionButtonGroup => {
    const item = rowData.rowSettings?.item as IntelligenceReportDrawerRow | undefined

    return {
      className: "",
      actionButtons: [
        {
          icon: <IconEye />,
          tooltip: "View report",
          tooltipPosition: "top",
          handler: () => {
            if (item) onViewReport(item)
          },
        },
        {
          icon: isMobile ? <IconFile /> : <FullReportText>Full Report</FullReportText>,
          tooltip: "Full Report",
          tooltipPosition: "top",
          className: isMobile ? "" : "fullReportAction",
          handler: async () => {
            if (!item) return
            try {
              const [{ generateReportPdf }, detailResult] = await Promise.all([
                import("../pdf/generateReportPdf"),
                dispatch(contractorsApi.endpoints.getIntelligenceReportDetail.initiate(item.report_id)).unwrap(),
              ])
              const blob = await generateReportPdf(detailResult.data)
              const url = URL.createObjectURL(blob)
              window.open(url, "_blank")
            } catch (err) {
              console.error("PDF generation failed:", err)
              toast.error("PDF download failed. Please try again later.")
            }
          },
        },
      ],
    }
  }

  return (
    <>
      <TableSection>
        <TableWrapper
          headerCells={intelligenceReportsTableHeaderCells}
          data={data}
          isLoading={isLoading}
          columnsLoaded={true}
          fitContainer={true}
          getRowActions={getRowActions}
          isMobileDevice={isMobile}
          skippedTags={["priority"]}
        />
      </TableSection>
      <Pagination meta={meta} />
    </>
  )
}
